import type { ActivityFeedItem } from './activityFeed';

interface MoodEntry {
  mood: string;
  label: string;
  name?: string;
}

export interface MoodData {
  [dateKey: string]: { [uid: string]: MoodEntry };
}

export interface WeeklyQuest {
  title: string;
  detail: string;
  cta: string;
  screen: string;
  targetTypes: string[];
  targetCount: number;
}

export function getWeekKey(date = new Date()) {
  const start = new Date(date.getFullYear(), 0, 1);
  const week = Math.ceil(((date.getTime() - start.getTime()) / 86400000 + start.getDay() + 1) / 7);
  return `${date.getFullYear()}-W${String(week).padStart(2, '0')}`;
}

function parseDay(dayKey: string) {
  const [y, m, d] = dayKey.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function isThisWeek(dayKey: string, now = new Date()) {
  const date = parseDay(dayKey);
  if (!date) return false;
  return getWeekKey(date) === getWeekKey(now);
}

export function getWeeklyQuest(now = new Date()): WeeklyQuest {
  const quests: WeeklyQuest[] = [
    {
      title: 'Bir kare görevi',
      detail: 'Bu hafta birbirinize bugünü hatırlatacak bir fotoğraf bırakın.',
      cta: 'Kare ekle',
      screen: 'photos',
      targetTypes: ['instant_photo'],
      targetCount: 1,
    },
    {
      title: 'Üç gece izi',
      detail: 'Bu hafta üç gece notu bırakın; kısa cümleler de sayılır.',
      cta: 'Not yaz',
      screen: 'notes',
      targetTypes: ['night_note'],
      targetCount: 3,
    },
    {
      title: 'Minik hafızası',
      detail: 'Bu hafta Minik’e iki bağ ritüeli emanet edin.',
      cta: 'Minik’e git',
      screen: 'pet',
      targetTypes: ['pet_ritual'],
      targetCount: 2,
    },
    {
      title: 'Hâl haritası',
      detail: 'Bu hafta üç gün bugünkü halinizi yan yana getirin.',
      cta: 'Hal seç',
      screen: 'mood',
      targetTypes: ['mood'],
      targetCount: 6,
    },
  ];
  const seed = getWeekKey(now).split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return quests[seed % quests.length];
}

export function getWeeklyQuestProgress(feed: ActivityFeedItem[], quest: WeeklyQuest) {
  const weekMatches = feed.filter(item => isThisWeek(item.dayKey) && quest.targetTypes.includes(item.type));
  const fallbackMatches = weekMatches.length
    ? weekMatches
    : feed.slice(0, 8).filter(item => quest.targetTypes.includes(item.type));
  const count = fallbackMatches.length;
  return {
    count,
    target: quest.targetCount,
    done: count >= quest.targetCount,
    percent: Math.min(100, Math.round((count / quest.targetCount) * 100)),
  };
}

export function buildWeeklySummary(feed: ActivityFeedItem[], moods: MoodData) {
  const exactWeekFeed = feed.filter(item => isThisWeek(item.dayKey));
  const weekFeed = exactWeekFeed.length ? exactWeekFeed : feed.slice(0, 8);
  const activeDays = new Set(weekFeed.map(item => item.dayKey)).size;
  const petMoments = weekFeed.filter(item => item.type === 'pet_care' || item.type === 'pet_ritual').length;
  const notes = weekFeed.filter(item => item.type === 'night_note').length;
  const photos = weekFeed.filter(item => item.type === 'instant_photo').length;
  const moodCounts = new Map<string, number>();

  Object.entries(moods).forEach(([dayKey, entry]) => {
    if (!isThisWeek(dayKey)) return;
    Object.values(entry).forEach(mood => {
      if (!mood?.label) return;
      moodCounts.set(mood.label, (moodCounts.get(mood.label) || 0) + 1);
    });
  });

  const topMood = Array.from(moodCounts.entries()).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
  const title = activeDays
    ? `${activeDays} gün iz kaldı`
    : 'Bu hafta daha başlamadı';
  const detail = topMood
    ? `En çok "${topMood}" hissi göründü. ${petMoments ? `Minik ${petMoments} kez sizi hatırladı.` : 'Minik bu haftadan iz bekliyor.'}`
    : activeDays
      ? `${notes} not, ${photos} kare, ${petMoments} Minik izi birikti.`
      : 'Bir soru, bir kare ya da Minik ritüeliyle ilk izi bırakabilirsiniz.';

  return {
    title,
    detail,
    activeDays,
    petMoments,
    notes,
    photos,
    topMood,
  };
}

export function buildPetMemory(feed: ActivityFeedItem[], petName: string, partner: string, myUid: string) {
  const latestPartner = feed.find(item => item.actorId !== myUid);
  if (latestPartner) {
    return `${petName}, ${latestPartner.actorName || partner} tarafından bırakılan "${latestPartner.title.toLocaleLowerCase('tr-TR')}" izini hatırlıyor.`;
  }

  const latest = feed[0];
  if (latest) {
    return `${petName}, en son ${latest.actorName} tarafından bırakılan küçük izi aklında tutuyor.`;
  }

  return `${petName} henüz bu haftadan bir anı seçemedi. İlk iz, ona ev hissi verecek.`;
}
